import { useEffect, useState } from 'react';
import { adminApi } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Link } from 'react-router-dom';
import { StatusPill } from '../../components/StatusPills';

const StatCard = ({ label, value, testId }) => (
  <div className="rounded-lg bg-ground p-5 min-w-0">
    <p className="pd-eyebrow">{label}</p>
    <p
      className="font-heading text-[32px] font-bold tracking-[-0.035em] leading-none text-ink mt-2"
      data-testid={testId}
    >
      {value ?? '—'}
    </p>
  </div>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .dashboard()
      .then((res) => !cancelled && setStats(res.data))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="font-heading text-2xl font-semibold text-foreground">Admin overview</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Top-level health of the canonical organisations database.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Canonical orgs"
          value={loading ? '…' : stats?.total_canonical_orgs}
          testId="stat-total-canonical-orgs"
        />
        <StatCard
          label="Pending verification"
          value={loading ? '…' : stats?.pending_verification}
          testId="stat-pending-verification"
        />
        <StatCard
          label="Missing privacy email"
          value={loading ? '…' : stats?.missing_privacy_email}
          testId="stat-missing-email"
        />
        <StatCard
          label="Total SARs (all users)"
          value={loading ? '…' : stats?.total_sars_all_users}
          testId="stat-total-sars"
        />
      </div>

      <Card>
        <CardContent className="p-5 space-y-3" data-testid="notable-behaviour-card">
          <div className="flex items-start gap-2.5">
            <div>
              <p className="font-medium text-foreground">Notable behaviour</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Organisations with statistically unusual patterns. Shown once there are five or more requests of evidence.
              </p>
            </div>
          </div>
          {(stats?.notable_behaviour || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {loading ? 'Loading…' : 'Nothing flagged yet. Flags appear once enough requests have been sent.'}
            </p>
          ) : (
            <ul className="space-y-2 pl-1">
              {stats.notable_behaviour.map((n) => (
                <li
                  key={n.id}
                  className="rounded-lg bg-ground p-3"
                  data-testid={`notable-${n.id}`}
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <Link to={`/admin/organisations/${n.id}`} className="font-medium text-foreground hover:underline">
                      {n.name}
                    </Link>
                    <span className="text-xs text-muted-foreground">
                      {n.type} · {n.total_sars} SARs
                    </span>
                  </div>
                  <ul className="mt-1.5 flex flex-wrap gap-1.5">
                    {n.flags.map((f, idx) => (
                      <li key={idx}>
                        <StatusPill tone="needs" size="sm">{f.label}</StatusPill>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
